import type { SmartCreationStep } from "@adpilot/shared";
import { IconAlertTriangle, IconSparkles } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { WizardProgress } from "@/components/patterns/WizardProgress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/** `reviewing` is the preview step with a ready Review Workspace, which owns its own chrome. */
export function WorkspaceHeader({
  shopName,
  currentStepIndex,
  reviewing,
}: {
  shopName: string;
  currentStepIndex: number;
  reviewing: boolean;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <header className="flex min-h-[88px] shrink-0 flex-wrap items-center justify-between gap-4 border-b border-line px-6 py-4 sm:h-[88px] sm:flex-nowrap">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2.5">
          {!reviewing ? <IconSparkles className="size-5 shrink-0" /> : null}
          <h1
            className={cn(
              "text-xl leading-[26px] tracking-[-0.02em]",
              reviewing ? "font-medium" : "font-bold",
            )}
          >
            {t("header.title")}
          </h1>
        </div>
        <p className="text-xs leading-[18px] text-muted-foreground">
          {t(reviewing ? "header.previewContext" : "header.localContext", { shop: shopName })}
        </p>
      </div>

      <WizardProgress
        currentStep={currentStepIndex + 1}
        labels={[
          t("steps.products"),
          t("steps.objectives"),
          t("steps.strategy"),
          t("steps.structure"),
          t("steps.preview"),
        ]}
        variant="compact"
        showLabels={reviewing}
      />
    </header>
  );
}

/** A submission was sent but its acceptance is unknown; the user retries or discards it. */
export function SubmissionPendingAlert({
  actionPending,
  discarding,
  onDiscard,
  onRetry,
}: {
  actionPending: boolean;
  discarding: boolean;
  onDiscard: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <Alert variant="warning" className="mx-6 my-4 w-auto">
      <IconAlertTriangle />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{t("task.submissionPending")}</span>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={actionPending}
            loading={discarding}
            onClick={onDiscard}
          >
            {t("task.discardSubmission")}
          </Button>
          <Button size="sm" variant="outline" disabled={actionPending} onClick={onRetry}>
            {t("task.retrySubmission")}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

interface WorkspaceFooterProps {
  canEdit: boolean;
  currentStep: SmartCreationStep;
  currentStepIndex: number;
  showCancel: boolean;
  cancelDialogOpen: boolean;
  onCancelDialogOpenChange: (open: boolean) => void;
  onKeepEditing: () => void;
  cancelPending: boolean;
  onCancelPlan: () => void;
  actionPending: boolean;
  actionFeedbackPending: boolean;
  stepActionsPending: boolean;
  contentBlocking: boolean;
  shopChanging: boolean;
  revisionConflict: boolean;
  nextDisabled: boolean;
  onBack: () => void;
  onNext: () => void;
}

/** Wizard footer: cancel-Plan confirmation on the left, Back / primary step action on the right. */
export function WorkspaceFooter({
  canEdit,
  currentStep,
  currentStepIndex,
  showCancel,
  cancelDialogOpen,
  onCancelDialogOpenChange,
  onKeepEditing,
  cancelPending,
  onCancelPlan,
  actionPending,
  actionFeedbackPending,
  stepActionsPending,
  contentBlocking,
  shopChanging,
  revisionConflict,
  nextDisabled,
  onBack,
  onNext,
}: WorkspaceFooterProps) {
  const { t } = useTranslation("smartCreation");
  return (
    <footer className="flex min-h-20 shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line bg-table-footer px-6 py-4">
      <div className="flex shrink-0 items-center gap-3">
        {showCancel ? (
          <Popover open={cancelDialogOpen} onOpenChange={onCancelDialogOpenChange}>
            <PopoverTrigger
              render={
                <Button
                  variant="ghost"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive active:bg-destructive/15"
                  size="sm"
                  disabled={actionPending || shopChanging}
                />
              }
            >
              {t("cancelDialog.action")}
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-64">
              <PopoverTitle className="text-sm">{t("cancelDialog.title")}</PopoverTitle>
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionPending}
                  onClick={onKeepEditing}
                >
                  {t("actions.keepEditing")}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={actionPending || revisionConflict}
                  onClick={onCancelPlan}
                >
                  {cancelPending ? <Spinner data-icon="inline-start" /> : null}
                  {t("cancelDialog.action")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
      <div data-smart-creation-step-actions className="flex shrink-0 items-center gap-2.5">
        {!contentBlocking && actionFeedbackPending ? (
          <span role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Spinner />
            {t("actions.savingChanges")}
          </span>
        ) : null}
        {canEdit && currentStepIndex > 0 ? (
          <Button
            variant="outline"
            disabled={stepActionsPending || revisionConflict || !canEdit}
            onClick={onBack}
          >
            {t("actions.back")}
          </Button>
        ) : null}
        <Button disabled={nextDisabled} onClick={onNext}>
          {contentBlocking ? <Spinner data-icon="inline-start" /> : null}
          {!canEdit
            ? t("actions.close")
            : contentBlocking && currentStep === "products"
              ? t("actions.checkingProducts")
              : primaryActionLabel(t, currentStep)}
        </Button>
      </div>
    </footer>
  );
}

function primaryActionLabel(t: ReturnType<typeof useTranslation>["t"], step: SmartCreationStep) {
  if (step === "products") return t("actions.nextObjectives");
  if (step === "objectives") return t("actions.nextStrategy");
  if (step === "strategy") return t("actions.nextStructure");
  if (step === "structure") return t("actions.generatePreview");
  return t("actions.confirm");
}
